import { Globe, Waves, Layers } from "lucide-react";

const REGIME_STYLES = {
  STRONG_RISK_ON:  { color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/40", label: "STRONG RISK-ON" },
  RISK_ON:         { color: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30", label: "RISK-ON" },
  NEUTRAL:         { color: "text-slate-300",   bg: "bg-slate-500/10",   border: "border-slate-500/30",   label: "NEUTRAL" },
  RISK_OFF:        { color: "text-red-300",     bg: "bg-red-500/10",     border: "border-red-500/30",     label: "RISK-OFF" },
  STRONG_RISK_OFF: { color: "text-red-300",     bg: "bg-red-500/10",     border: "border-red-500/40",     label: "STRONG RISK-OFF" },
};

function fmt(v, d = 2) {
  if (v == null) return "—";
  return Number(v).toFixed(d);
}

function pctSpan(v) {
  if (v == null) return <span className="text-slate-500">—</span>;
  const cls = v > 0 ? "text-emerald-300" : v < 0 ? "text-red-300" : "text-slate-300";
  return <span className={cls}>{v >= 0 ? "+" : ""}{v.toFixed(2)}%</span>;
}

function trendChip(trend) {
  const cls =
    trend === "bullish"
      ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
      : trend === "bearish"
      ? "text-red-300 border-red-500/40 bg-red-500/10"
      : trend === "insufficient"
      ? "text-amber-300 border-amber-500/40 bg-amber-500/10"
      : "text-slate-300 border-slate-500/40 bg-slate-500/10";
  return (
    <span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${cls}`}>
      {trend === "insufficient" ? "n/a" : trend || "—"}
    </span>
  );
}

export default function ContextRow({ result }) {
  if (!result) return null;
  const regime = result.market_regime || {};
  const rs = result.relative_strength || {};
  const align = result.timeframe_alignment || {};
  const perTF = align.per_timeframe || {};
  const rStyle = REGIME_STYLES[regime.regime] || REGIME_STYLES.NEUTRAL;

  const rsPairs = [
    { key: "vs_spy", label: "vs SPY" },
    { key: "vs_qqq", label: "vs QQQ" },
    { key: "vs_sector", label: `vs ${result.sector_etf || "Sector"}` },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {/* Market regime */}
      <div data-testid="context-regime" className="border border-[#1E293B] bg-[#0F1626]">
        <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C] flex items-center gap-2">
          <Globe className="h-3.5 w-3.5 text-cyan-400" />
          <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Market Regime</span>
        </div>
        <div className="p-3">
          <div className={`inline-block mono text-xs px-2 py-1 border ${rStyle.border} ${rStyle.bg} ${rStyle.color} uppercase tracking-wider`}>
            {rStyle.label}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 mono text-[10px]">
            <div className="flex justify-between border-b border-[#151C2C] py-1"><span className="text-slate-500">Score</span><span className="text-slate-200">{fmt(regime.score, 1)}</span></div>
            <div className="flex justify-between border-b border-[#151C2C] py-1"><span className="text-slate-500">Character</span><span className="text-slate-200">{regime.character?.trending ? "TRENDING" : "RANGE"}</span></div>
            <div className="flex justify-between border-b border-[#151C2C] py-1"><span className="text-slate-500">Volatility</span><span className={regime.character?.high_vol ? "text-amber-300" : "text-slate-200"}>{regime.character?.high_vol ? "ELEVATED" : "NORMAL"}</span></div>
            <div className="flex justify-between border-b border-[#151C2C] py-1"><span className="text-slate-500">ADX (SPY)</span><span className="text-slate-200">{fmt(regime.character?.adx_spy, 2)}</span></div>
          </div>
          {regime.per_index && (
            <div className="mt-2 pt-2 border-t border-[#151C2C] grid grid-cols-3 gap-1 mono text-[10px]">
              {["SPY", "QQQ", "IWM"].map((k) => (
                <div key={k} className="flex flex-col items-center">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-slate-200">{fmt(regime.per_index[k]?.score, 0)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Relative strength */}
      <div data-testid="context-rs" className="border border-[#1E293B] bg-[#0F1626]">
        <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C] flex items-center gap-2">
          <Waves className="h-3.5 w-3.5 text-cyan-400" />
          <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Relative Strength</span>
        </div>
        <div className="p-3 space-y-2">
          <table className="q-table">
            <thead>
              <tr>
                <th>Bench</th>
                <th className="right">5D</th>
                <th className="right">20D</th>
                <th className="right">60D</th>
                <th className="right">Dir</th>
              </tr>
            </thead>
            <tbody>
              {rsPairs.map((p) => {
                const pair = rs[p.key] || {};
                const diffs = pair.diffs || {};
                const cls = pair.classification || {};
                const dirCls = cls.direction === "outperforming" ? "text-emerald-300" : cls.direction === "underperforming" ? "text-red-300" : "text-slate-500";
                return (
                  <tr key={p.key}>
                    <td className="text-slate-100">{p.label}</td>
                    <td className="right">{pctSpan(diffs.d5)}</td>
                    <td className="right">{pctSpan(diffs.d20)}</td>
                    <td className="right">{pctSpan(diffs.d60)}</td>
                    <td className={`right ${dirCls} uppercase text-[9px]`}>
                      {cls.direction === "outperforming" ? "OUT" : cls.direction === "underperforming" ? "UND" : "—"}
                      {cls.accelerating != null && (
                        <span className="ml-1 text-slate-500 text-[9px]">
                          {cls.accelerating ? "↑" : "↓"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Timeframe alignment */}
      <div data-testid="context-tf-alignment" className="border border-[#1E293B] bg-[#0F1626]">
        <div className="px-3 py-2 border-b border-[#1E293B] bg-[#0B111C] flex items-center gap-2">
          <Layers className="h-3.5 w-3.5 text-cyan-400" />
          <span className="mono text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Timeframe Alignment</span>
        </div>
        <div className="p-3">
          <div className="flex items-center justify-between mono text-[10px] mb-3">
            <span className="text-slate-500 uppercase tracking-widest">Direction</span>
            <span data-testid="tf-alignment-direction" className={
              align.direction === "long" ? "text-emerald-300" :
              align.direction === "short" ? "text-red-300" : "text-slate-300"
            }>
              {(align.direction || "mixed").toUpperCase()} · L {fmt(align.long_score, 0)} / S {fmt(align.short_score, 0)}
            </span>
          </div>
          <div className="space-y-1.5">
            {[
              { k: "weekly", label: "Weekly" },
              { k: "daily", label: "Daily" },
              { k: "h4", label: "4H" },
              { k: "h1", label: "1H" },
            ].map((t) => {
              const info = perTF[t.k] || {};
              return (
                <div key={t.k} className="flex items-center justify-between border-b border-[#151C2C] pb-1 last:border-b-0">
                  <span className="mono text-[10px] text-slate-400 w-16">{t.label}</span>
                  <div className="flex-1 mx-2">{trendChip(info.trend)}</div>
                  <span className="mono text-[10px] text-slate-500 tabular-nums w-16 text-right">
                    L {fmt(info.long_score, 0)} / S {fmt(info.short_score, 0)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
